import axios from 'axios';
import { resolveTenantSlug } from '../hooks/useTenant';
import { isNativeApp } from '../utils/nativeApp';
import { clearAllFlowItems } from '../utils/oauthFlowStorage';
import referenceDataService from './referenceDataService';
import utilityBillCacheService from './utilityBillCacheService';
import correspondenceCacheService from './correspondenceCacheService';
import propertyCacheService from './propertyCacheService';
import bankAccountCacheService from './bankAccountCacheService';
import loanCacheService from './loanCacheService';
import zaehlerCacheService from './zaehlerCacheService';
import zaehlerArtCacheService from './zaehlerArtCacheService';
import objektkostenService from './objektkostenService';
import anlageVCacheService from './anlageVCacheService';
import statistikCacheService from './statistikCacheService';
import wasserVerbrauchService from './wasserVerbrauchService';

import { getApiUrl } from '../utils/runtimeConfig';
// eslint-disable-next-line no-unused-vars
import { SESSION_EXPIRED_STORAGE_KEY } from '../utils/errorHandling'; // intentionally not removed on logout (survives redirect to /login)

const API_BASE = getApiUrl();
const API_URL = `${API_BASE}/auth`;

/**
 * Clears all user-specific caches before persisting a new token.
 * Must be called BEFORE sessionStorage.setItem('token', ...) at every
 * token-change path (login, register, OAuth exchange, link-account, reset-password).
 */
const clearAllUserCaches = () => {
  referenceDataService.clearCache();
  utilityBillCacheService.clearCache();
  correspondenceCacheService.clearCache();
  propertyCacheService.clearCache();
  bankAccountCacheService.clearCache();
  loanCacheService.clearCache();
  zaehlerCacheService.clearCache();
  zaehlerArtCacheService.clearCache();
  objektkostenService.clearCache();
  anlageVCacheService.clearCache();
  statistikCacheService.clearCache();
  wasserVerbrauchService.clearCache();
};

/**
 * Storage used for the auth session (token/user).
 *
 * Native app (Capacitor): localStorage. sessionStorage is tied to the
 * WebView's browsing session and is not a reliable place to keep a native
 * app's login across the app being backgrounded/resumed by the OS the way a
 * native app's process lifecycle requires — localStorage survives that.
 * Web (default): sessionStorage — UNCHANGED behaviour from before native
 * support was added; a browser tab ending its session must still end the
 * login exactly as it always has.
 *
 * @returns {Storage}
 */
const getAuthStorage = () => (isNativeApp() ? window.localStorage : window.sessionStorage);

/**
 * Removes the auth session (token/user) from BOTH storages unconditionally,
 * regardless of the current platform. Used on logout/session-clear so a
 * lingering session can never survive in the storage the platform switch
 * (native vs. web testing, etc.) didn't happen to be using at write time.
 * Defensive try/catch: localStorage/sessionStorage can throw in some
 * Capacitor/privacy-mode environments (see InstallAppBanner.js).
 */
const clearAuthStorageBoth = () => {
  [window.sessionStorage, window.localStorage].forEach((storage) => {
    try {
      storage.removeItem('token');
      storage.removeItem('user');
    } catch (e) {
      // Storage unavailable — nothing more we can do here.
    }
  });
};

/**
 * Shared helper: clears all user caches and then persists the new session.
 * Only writes to storage when BOTH token and user are present to
 * prevent "undefined" strings being stored (e.g. JSON.stringify(undefined)).
 *
 * Used by login, resetPassword, OAuth exchange/link and emailVerificationService
 * so that cross-user data leaks (User A's cached data shown to User B) are
 * impossible after any auth-session change.
 *
 * @param {string} token  - JWT access token
 * @param {object} user   - User object returned by the server
 */
const persistAuthSession = (token, user) => {
  if (!token || !user) return;
  clearAllUserCaches();
  getAuthStorage().setItem('token', token);
  getAuthStorage().setItem('user', JSON.stringify(user));
};

/**
 * Custom error class for account exists responses (409 Conflict).
 */
export class AccountExistsError extends Error {
  constructor(data) {
    super(data.message || 'Account already exists');
    this.name = 'AccountExistsError';
    this.data = data;
  }
}

const authService = {
  login: async (identifier, password, tenantSlug) => {
    const slug = resolveTenantSlug(tenantSlug);
    const response = await axios.post(`${API_URL}/${slug}/login`, { identifier, password });
    persistAuthSession(response.data.token, response.data.user);
    return response.data;
  },

  register: async (userData, tenantSlug) => {
    const slug = resolveTenantSlug(tenantSlug);
    const response = await axios.post(`${API_URL}/${slug}/register`, userData);
    // Two possible outcomes depending on the tenant's SMTP configuration:
    // - SMTP enabled  → verificationRequired === true, no token/user in response.
    //                   Purge any stale session; user must confirm via e-mail.
    // - SMTP disabled → verificationRequired === false, token + user returned.
    //                   Persist session immediately (auto-login).
    if (response.data.token && response.data.user) {
      persistAuthSession(response.data.token, response.data.user);
    } else {
      clearAllUserCaches();
      clearAuthStorageBoth();
    }
    return response.data;
  },

  logout: () => {
    // Always clears BOTH storages (see clearAuthStorageBoth) — independent of
    // isNativeApp() at the moment of logout, so no session can survive.
    clearAuthStorageBoth();
    // Same "always both storages" reasoning for the OAuth-flow markers
    // (state/provider/pkce-verifier/linking/delete-reauth) — clearAllFlowItems
    // removes each from BOTH sessionStorage and localStorage regardless of
    // platform. Includes the delete-account re-auth markers: must be cleared
    // on logout so that an abandoned deletion flow can never silently
    // piggy-back on a later normal OAuth login (which would otherwise route
    // into the destructive delete branch).
    clearAllFlowItems();
    // SESSION_EXPIRED_STORAGE_KEY is intentionally NOT removed here so that
    // the message survives the redirect to /login and can be shown there.
    clearAllUserCaches();
  },

  getCurrentUser: () => {
    const userStr = getAuthStorage().getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      getAuthStorage().removeItem('user');
      return null;
    }
  },

  getToken: () => {
    return getAuthStorage().getItem('token');
  },

  // --- OAuth methods ---

  /**
   * Get OAuth redirect URL for a provider.
   * @param {string} provider - OAuth provider (GOOGLE, MICROSOFT, APPLE, FACEBOOK)
   * @param {string} tenantSlug - Tenant slug
   * @param {{ flow?: string, redirectUri?: string, codeChallenge?: string }} [options] -
   *   `flow`: optional flow marker, e.g. 'DELETE_REAUTH' to have the backend route
   *   the callback into the account-deletion re-auth path instead of a normal login.
   *   `redirectUri`/`codeChallenge`: native (Capacitor) custom-scheme PKCE flow —
   *   see utils/oauthStart.js. Both are sent only when actually provided so the
   *   web flow's request shape stays byte-identical to before.
   * @returns {Promise<string>} The OAuth redirect URL
   */
  getOAuthRedirectUrl: async (provider, tenantSlug, options) => {
    // Destructuring-default (`{ flow } = {}`) only covers a missing/undefined third
    // argument — an explicitly passed `null` would still crash. `options || {}`
    // covers both.
    const { flow, redirectUri, codeChallenge } = options || {};
    const slug = resolveTenantSlug(tenantSlug);
    const params = { provider, tenantSlug: slug };
    if (flow) {
      params.flow = flow;
    }
    if (redirectUri) {
      params.redirectUri = redirectUri;
    }
    if (codeChallenge) {
      params.codeChallenge = codeChallenge;
    }
    const response = await axios.get(`${API_URL}/oauth/redirect-url`, { params });
    return response.data.redirectUrl;
  },

  /**
   * Exchange a one-time code for JWT token (BFF pattern).
   * @param {string} oneTimeCode - One-time code from OAuth callback
   * @param {string} [codeVerifier] - PKCE code verifier; required when the
   *   one-time code was created with a code challenge (native custom-scheme
   *   flow, see utils/oauthStart.js). Omitted entirely for the web flow.
   * @returns {Promise<object>} Auth response with token and user
   */
  exchangeOAuthCode: async (oneTimeCode, codeVerifier = null) => {
    try {
      const payload = { oneTimeCode };
      if (codeVerifier) {
        payload.codeVerifier = codeVerifier;
      }
      const response = await axios.post(`${API_URL}/oauth/exchange`, payload);
      persistAuthSession(response.data.token, response.data.user);
      return response.data;
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.status === 'ACCOUNT_EXISTS') {
        throw new AccountExistsError(error.response.data);
      }
      throw error;
    }
  },

  /**
   * Link an existing account with password verification (Path A).
   * @param {string} linkingToken - Linking token from account exists response
   * @param {string} password - User's password
   * @returns {Promise<object>} Auth response with token and user
   */
  linkAccount: async (linkingToken, password) => {
    const response = await axios.post(`${API_URL}/oauth/link-account`, {
      linkingToken,
      password
    });
    persistAuthSession(response.data.token, response.data.user);
    return response.data;
  },

  /**
   * Link an existing account with OAuth re-authentication (Path B).
   * @param {string} linkingToken - Linking token
   * @param {string} reAuthCode - Authorization code from re-auth flow
   * @param {string} reAuthProvider - Provider used for re-auth
   * @param {string} [reAuthCodeVerifier] - PKCE code verifier matching the code
   *   challenge sent when the re-auth redirect URL was requested. Only relevant
   *   for the native (Capacitor) custom-scheme flow (see utils/oauthStart.js);
   *   omitted entirely for the web flow, which never sets a challenge.
   * @returns {Promise<object>} Auth response with token and user
   */
  linkAccountWithOAuth: async (linkingToken, reAuthCode, reAuthProvider, reAuthCodeVerifier) => {
    const payload = { linkingToken, reAuthCode, reAuthProvider };
    if (reAuthCodeVerifier) {
      payload.reAuthCodeVerifier = reAuthCodeVerifier;
    }
    const response = await axios.post(`${API_URL}/oauth/link-account`, payload);
    persistAuthSession(response.data.token, response.data.user);
    return response.data;
  },

  /**
   * Get tenant authentication config (public endpoint).
   * @param {string} tenantSlug - Tenant slug
   * @returns {Promise<object>} Auth config
   */
  getTenantAuthConfig: async (tenantSlug) => {
    const slug = resolveTenantSlug(tenantSlug);
    const response = await axios.get(`${API_URL}/${slug}/auth-config`);
    return response.data;
  },

  /**
   * Persist an updated user object in the auth storage without a full login.
   * Used after profile changes (username, profile picture, etc.).
   * @param {Object} user - Updated user object
   */
  updateStoredUser: (user) => {
    getAuthStorage().setItem('user', JSON.stringify(user));
  },

  /**
   * Persist a new JWT token in the auth storage without a full login.
   * Used after a username change which issues a new token.
   * @param {string} token - New JWT token
   */
  updateStoredToken: (token) => {
    getAuthStorage().setItem('token', token);
  },

  /**
   * Request a password reset e-mail for the given username or e-mail address.
   * @param {string} identifier - The user's username or e-mail address
   * @param {string} tenantSlug - Tenant slug
   * @returns {Promise<object>} Server response
   */
  forgotPassword: async (identifier, tenantSlug) => {
    const slug = resolveTenantSlug(tenantSlug);
    const response = await axios.post(`${API_URL}/${slug}/forgot-password`, { identifier });
    return response.data;
  },

  /**
   * Reset the password using the token received via e-mail.
   * On success the response contains a JWT token and the user is logged in.
   * @param {string} token - Password-reset token from the e-mail link
   * @param {string} newPassword - The new password chosen by the user
   * @param {string} tenantSlug - Tenant slug
   * @returns {Promise<object>} Auth response with optional token and user
   */
  resetPassword: async (token, newPassword, tenantSlug) => {
    const slug = resolveTenantSlug(tenantSlug);
    const response = await axios.post(`${API_URL}/${slug}/reset-password`, { token, newPassword });
    persistAuthSession(response.data.token, response.data.user);
    return response.data;
  }
};

export { persistAuthSession };

export default authService;
